// Lets a cashier who has logged in on this device before get back into the
// till with no internet. We never keep the password or passcode: only a salted
// PBKDF2 hash to check what they type against, plus the last login response so
// the session can be rebuilt. Cashiers only; other roles still need the server.

const KEY = "01pos_offline_logins";
const ITERATIONS = 100000;

const toHex = (buf) =>
  Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

const fromHex = (hex) =>
  new Uint8Array(hex.match(/.{2}/g).map((h) => parseInt(h, 16)));

const secretOf = (body) => (body?.login_pin ? body.login_pin : body?.password || "");

const hash = async (secret, salt) => {
  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    "PBKDF2",
    false,
    ["deriveBits"]
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt, iterations: ITERATIONS, hash: "SHA-256" },
    key,
    256
  );
  return toHex(bits);
};

// Email logins are keyed by email; a passcode is its own identifier, so it is
// keyed by a digest of the passcode rather than the passcode itself.
const storeKey = async (body) => {
  if (!body?.login_pin) return `email:${(body?.email || "").trim().toLowerCase()}`;
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(body.login_pin)
  );
  return `pin:${toHex(digest)}`;
};

const readAll = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || {};
  } catch {
    return {};
  }
};

export const rememberLogin = async (body, data) => {
  try {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const all = readAll();
    all[await storeKey(body)] = {
      salt: toHex(salt),
      hash: await hash(secretOf(body), salt),
      data,
    };
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    // Offline login is a convenience; never block a real login.
  }
};

// Returns the saved login response when the credentials match, else null.
export const verifyOfflineLogin = async (body) => {
  try {
    const entry = readAll()[await storeKey(body)];
    if (!entry) return null;
    const attempt = await hash(secretOf(body), fromHex(entry.salt));
    return attempt === entry.hash ? entry.data : null;
  } catch {
    return null;
  }
};
